import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import { ListingAvailableOptions } from '@nocowanie/noc-api';

import { FILTER_CONFIG } from '@app/components/molecules/map-active-filters/map-active-filters.config';
import { ReservationSwitchProps } from '@app/components/molecules/reservation-switch';
import {
    createFilterItems,
    createReservationSwitchWithConfig,
} from '@app/components/organisms/filter-items';
import { adjustOnlineBookingFilters } from '@app/helpers/filters.helpers';
import { useSearch } from '@app/hooks/useSearch';
import { SearchFilters } from '@app/models';

/**
 * Hook for search filters
 */
export function useSearchFilters({
    availableOptions,
    withCounter,
}: {
    availableOptions?: ListingAvailableOptions | null;
    withCounter?: boolean;
}) {
    const { filters, updateSearchFilters, isMobileRes } = useSearch();
    const [localFilters, setLocalFilters] = useState<SearchFilters>(filters);
    const isInteractingRef = useRef(false);
    const accumulatedFiltersRef = useRef<Partial<SearchFilters>>({});

    const debouncedUpdateFilters = useMemo(
        () =>
            debounce(() => {
                updateSearchFilters({ ...accumulatedFiltersRef.current }, true);
                accumulatedFiltersRef.current = {};

                requestAnimationFrame(() => {
                    isInteractingRef.current = false;
                });
            }, 250),
        [updateSearchFilters],
    );

    const handleFilterChange = useCallback(
        <K extends keyof SearchFilters>(
            filterId: K,
            values: SearchFilters[K],
            forceSubmitOnMobile = false,
        ) => {
            isInteractingRef.current = true;

            const updatedFilters: Partial<SearchFilters> = {
                [filterId]: values,
            };

            const prevAccumulatedFilters = { ...accumulatedFiltersRef.current };
            accumulatedFiltersRef.current = adjustOnlineBookingFilters(prevAccumulatedFilters, {
                ...accumulatedFiltersRef.current,
                ...updatedFilters,
            });

            requestAnimationFrame(() => {
                setLocalFilters(prev => {
                    return adjustOnlineBookingFilters(prev, { ...prev, ...updatedFilters });
                });
                if (!forceSubmitOnMobile && isMobileRes) {
                    // On mobile, only update local state, actual update happens on "Search"
                    return;
                }
                debouncedUpdateFilters();
            });
        },
        [debouncedUpdateFilters, isMobileRes],
    );

    const filterItems = useMemo(
        () =>
            createFilterItems({
                availableOptions,
                localFilters,
                onFilterChange: handleFilterChange,
                withCounter,
            }),
        [availableOptions, localFilters, handleFilterChange, withCounter],
    );

    const handleApplyFilters = useCallback(() => {
        // On mobile, we want to apply new filters only when the user clicks 'Search'.
        // Fetching data in the background on every filter change doesn't make sense.
        isInteractingRef.current = false;
        if (!isEqual(localFilters, filters)) {
            updateSearchFilters(localFilters, true);
        }
    }, [localFilters, filters, updateSearchFilters]);

    const handleClearFilters = useCallback(
        (withStateUpdate?: boolean) => {
            requestAnimationFrame(() => {
                const clearedFilters = FILTER_CONFIG.defaults.getDefaultFilters();
                setLocalFilters(clearedFilters);
                if (withStateUpdate) {
                    setTimeout(() => {
                        updateSearchFilters(clearedFilters, true);
                    }, 0);
                }
            });
        },
        [updateSearchFilters],
    );

    const hasInstantBookingFilter = useMemo(
        () => availableOptions?.filters?.some(filter => filter.key === 'InstantBooking') ?? false,
        [availableOptions],
    );

    const createReservationSwitch = useCallback(
        (config: ReservationSwitchProps, forceSubmitOnMobile = false) => {
            if (!hasInstantBookingFilter) {
                return null;
            }

            return createReservationSwitchWithConfig({
                config,
                localFilters,
                onFilterChange: (filterId, values) =>
                    handleFilterChange(filterId, values, forceSubmitOnMobile),
            });
        },
        [hasInstantBookingFilter, localFilters, handleFilterChange],
    );

    useEffect(() => {
        if (!isInteractingRef.current) {
            setLocalFilters(filters);
        }
    }, [filters]);

    return {
        filterItems,
        createReservationSwitch,
        handleApplyFilters,
        handleClearFilters,
    };
}
